import { supabase } from '@/lib/supabase';
import { normalizeVoters, toAppRelativeLink } from '@/utils/pollUtils';

const UUID_FALLBACK = () => Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);

export const buildPollShareUrl = (token) => {
  if (!token) return '';
  const origin = typeof window !== 'undefined' && window?.location?.origin
    ? window.location.origin
    : '';
  return `${origin}/shared-poll/${encodeURIComponent(token)}`;
};

// `forCoach`: the link from the coach's own Polls page. A choice picked in
// discovery saves its menu link with sharedView=poll in it, the read-only
// view built for guests; a signed-in coach following it got no cart and no
// way to order the winner. Their link goes to the ordinary menu instead.
//
// A menu link in the app comes back as a path, whatever origin it was saved
// with (older polls saved their creator's), so it opens where the reader is.
export const buildPollOptionLink = (rawLink, shareToken, { forCoach = false } = {}) => {
  if (!rawLink || typeof rawLink !== 'string') return null;
  const trimmed = toAppRelativeLink(rawLink.trim());
  if (!trimmed) return null;
  const isAbsolute = /^https?:\/\//i.test(trimmed);
  const looksLikeLink = isAbsolute || trimmed.startsWith('/') || trimmed.startsWith('./');
  if (!looksLikeLink) return null;
  try {
    // The base only parses a path; a path is what comes back.
    const url = isAbsolute
      ? new URL(trimmed)
      : new URL(trimmed.startsWith('/') ? trimmed : `/${trimmed}`, 'https://mealops.app');
    const result = () => (isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`);
    if (forCoach) {
      url.searchParams.delete('sharedView');
      url.searchParams.delete('sharedPollToken');
      return result();
    }
    if (!url.searchParams.has('sharedView')) {
      url.searchParams.set('sharedView', 'poll');
    }
    if (shareToken) {
      url.searchParams.set('sharedPollToken', shareToken);
    }
    return result();
  } catch (error) {
    console.warn('Invalid poll option link', error);
    return null;
  }
};

const normalizeOption = (row) => {
  if (!row) return null;
  const votes = Array.isArray(row.poll_votes) ? row.poll_votes : [];
  const normalizedVoters = normalizeVoters(
    votes.map((vote) => ({
      memberId: vote.member_id || vote.user_id || vote?.member?.id,
      fullName: vote?.member?.full_name,
      email: vote?.member?.email,
      role: vote?.member?.role,
    }))
  );
  return {
    id: row.id,
    pollId: row.poll_id,
    label: row.restaurant_name,
    description: row.description || '',
    cuisineType: row.cuisine_type || '',
    createdAt: row.created_at || null,
    voteCount: votes.length,
    voters: normalizedVoters,
  };
};

const normalizePoll = (row) => {
  if (!row) return null;
  const options = Array.isArray(row.poll_options)
    ? row.poll_options.map(normalizeOption).filter(Boolean)
    : [];
  const totalVotes = options.reduce((acc, option) => acc + (option.voteCount || 0), 0);
  return {
    id: row.id,
    teamId: row.team_id,
    title: row.title,
    description: row.description || '',
    customMessage: row.custom_message || '',
    allowMultiple: Boolean(row.allow_multiple),
    allowSuggestions: Boolean(row.allow_suggestions),
    anonymous: Boolean(row.anonymous),
    targetRoles: Array.isArray(row.target_roles) ? row.target_roles : [],
    expiresAt: row.expires_at || null,
    status: row.poll_status,
    createdBy: row.created_by || null,
    createdAt: row.created_at || null,
    updatedAt: row.updated_at || null,
    shareToken: row.share_token || null,
    shareExpiresAt: row.share_expires_at || null,
    totalVotes,
    options,
    viewer: row.viewer || null,
  };
};

export async function fetchTeamPolls(teamId) {
  if (!teamId) return [];

  const { data, error } = await supabase
    .from('meal_polls')
    .select(`
      *,
      poll_options (
        id,
        poll_id,
        restaurant_name,
        cuisine_type,
        description,
        created_at,
        poll_votes:poll_votes!poll_votes_option_id_fkey (
          id,
          poll_id,
          option_id,
          member_id,
          user_id,
          member:team_members!poll_votes_member_id_fkey (
            id,
            full_name,
            email,
            role
          )
        )
      )
    `)
    .eq('team_id', teamId)
    .order('created_at', { ascending: false })
    // id breaks ties: options saved by one insert share a created_at.
    .order('created_at', { ascending: true, foreignTable: 'poll_options' })
    .order('id', { ascending: true, foreignTable: 'poll_options' });

  if (error) throw error;
  return (data || []).map(normalizePoll);
}

export async function fetchPollById(pollId) {
  if (!pollId) return null;
  const { data, error } = await supabase
    .from('meal_polls')
    .select(`
      *,
      poll_options (
        id,
        poll_id,
        restaurant_name,
        cuisine_type,
        description,
        created_at,
        poll_votes:poll_votes!poll_votes_option_id_fkey (
          id,
          poll_id,
          option_id,
          member_id,
          user_id,
          member:team_members!poll_votes_member_id_fkey (
            id,
            full_name,
            email,
            role
          )
        )
      )
    `)
    .eq('id', pollId)
    .order('created_at', { ascending: true, foreignTable: 'poll_options' })
    .order('id', { ascending: true, foreignTable: 'poll_options' })
    .maybeSingle();
  if (error) throw error;
  return normalizePoll(data);
}

const toTeamRolesArray = (audience) => {
  if (Array.isArray(audience) && audience.length) {
    return audience;
  }
  switch (audience) {
    case 'players':
      return ['player'];
    case 'staff':
      return ['staff'];
    case 'all':
    default:
      return ['player', 'staff', 'coach'];
  }
};

export async function createPoll({
  teamId,
  title,
  description = '',
  customMessage = '',
  options = [],
  expiresAt,
  allowMultiple = false,
  allowSuggestions = false,
  anonymous = false,
  targetAudience = 'all',
}) {
  if (!teamId) throw new Error('Missing team id');
  if (!title?.trim()) throw new Error('Poll title is required');
  if (!Array.isArray(options) || options.length === 0) {
    throw new Error('At least one poll option is required');
  }
  if (!expiresAt) {
    throw new Error('Expiration date/time is required');
  }

  const targetRoles = toTeamRolesArray(targetAudience);

  const { data: pollRow, error: pollError } = await supabase
    .from('meal_polls')
    .insert({
      team_id: teamId,
      title: title.trim(),
      description: description || '',
      custom_message: customMessage || '',
      poll_status: 'active',
      expires_at: expiresAt,
      allow_multiple: allowMultiple,
      allow_suggestions: allowSuggestions,
      anonymous,
      target_roles: targetRoles,
    })
    .select()
    .single();

  if (pollError) throw pollError;

  // Every reader (this page, the guest RPCs) sorts options by created_at,
  // and one insert gives every row the same default timestamp, so options
  // came back in a different order than typed, and in different orders on
  // the coach's card and the guest page. Each gets its own, a millisecond
  // apart, starting from the poll's.
  const baseMs = Date.parse(pollRow.created_at) || Date.now();
  const normalizedOptions = options
    .filter((option) => option?.label?.trim())
    .map((option, index) => ({
      poll_id: pollRow.id,
      restaurant_name: option.label.trim(),
      // A menu link is saved as its path, never with this browser's origin.
      description: option.description ? toAppRelativeLink(option.description.trim()) : null,
      cuisine_type: option.cuisineType ? option.cuisineType.trim() : null,
      created_at: new Date(baseMs + index + 1).toISOString(),
    }));

  if (normalizedOptions.length === 0) {
    throw new Error('Unable to create poll options');
  }

  const { error: optionsError } = await supabase
    .from('poll_options')
    .insert(normalizedOptions);

  if (optionsError) throw optionsError;

  return fetchPollById(pollRow.id);
}

export async function updatePoll(
  pollId,
  {
    title,
    description = '',
    expiresAt,
    allowMultiple = false,
    allowSuggestions = false,
    anonymous = false,
    options = [],
    removedOptionIds = [],
  }
) {
  if (!pollId) throw new Error('Missing poll id');
  if (!title?.trim()) throw new Error('Poll title is required');
  if (!expiresAt) throw new Error('Expiration date/time is required');

  const normalizedOptions = Array.isArray(options)
    ? options
        .map((option) => ({
          label: (option?.label || '').trim(),
          persistedId: option?.persistedId ?? null,
          // Saving an older poll also drops the origin from its menu links.
          description: option?.description ? toAppRelativeLink(option.description.trim()) : '',
          cuisineType: option?.cuisineType ? option.cuisineType.trim() : '',
        }))
        .filter((option) => option.label.length > 0)
    : [];

  if (normalizedOptions.length < 2) {
    throw new Error('At least two options are required.');
  }

  const { data: pollRow, error: pollError } = await supabase
    .from('meal_polls')
    .update({
      title: title.trim(),
      description: description || '',
      expires_at: expiresAt,
      allow_multiple: allowMultiple,
      allow_suggestions: allowSuggestions,
      anonymous,
    })
    .eq('id', pollId)
    .select('id')
    .single();

  if (pollError) throw pollError;
  if (!pollRow) throw new Error('Poll not found.');

  const existingOptions = normalizedOptions.filter((option) => option.persistedId);
  for (const option of existingOptions) {
    const { error: optionError } = await supabase
      .from('poll_options')
      .update({
        restaurant_name: option.label,
        description: option.description || null,
        cuisine_type: option.cuisineType || null,
      })
      .eq('id', option.persistedId);
    if (optionError) throw optionError;
  }

  const newOptions = normalizedOptions.filter((option) => !option.persistedId);
  if (newOptions.length > 0) {
    // Added options go after the ones already saved (the form only appends),
    // a millisecond apart, so they keep the order they were typed in.
    const { data: lastOption, error: lastOptionError } = await supabase
      .from('poll_options')
      .select('created_at')
      .eq('poll_id', pollId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (lastOptionError) throw lastOptionError;
    const baseMs = Math.max(Date.parse(lastOption?.created_at) || 0, Date.now());
    const insertPayload = newOptions.map((option, index) => ({
      poll_id: pollId,
      restaurant_name: option.label,
      description: option.description || null,
      cuisine_type: option.cuisineType || null,
      created_at: new Date(baseMs + index + 1).toISOString(),
    }));
    const { error: insertError } = await supabase.from('poll_options').insert(insertPayload);
    if (insertError) throw insertError;
  }

  const optionsToDelete = Array.isArray(removedOptionIds)
    ? removedOptionIds.filter((id) => id !== null && id !== undefined)
    : [];
  if (optionsToDelete.length > 0) {
    const { error: deleteError } = await supabase
      .from('poll_options')
      .delete()
      .eq('poll_id', pollId)
      .in('id', optionsToDelete);
    if (deleteError) throw deleteError;
  }

  return fetchPollById(pollId);
}

export async function updatePollStatus(pollId, status) {
  if (!pollId) throw new Error('Missing poll id');
  const { data, error } = await supabase
    .from('meal_polls')
    .update({ poll_status: status })
    .eq('id', pollId)
    .select(`
      *,
      poll_options (
        id,
        poll_id,
        restaurant_name,
        cuisine_type,
        description,
        created_at,
        poll_votes:poll_votes!poll_votes_option_id_fkey (
          id,
          poll_id,
          option_id,
          member_id,
          user_id,
          member:team_members!poll_votes_member_id_fkey (
            id,
            full_name,
            email,
            role
          )
        )
      )
    `)
    .order('created_at', { ascending: true, foreignTable: 'poll_options' })
    .order('id', { ascending: true, foreignTable: 'poll_options' })
    .single();
  if (error) throw error;
  return normalizePoll(data);
}

export async function deletePoll(pollId) {
  if (!pollId) return;
  const { error } = await supabase.from('meal_polls').delete().eq('id', pollId);
  if (error) throw error;
}

export async function regeneratePollShareToken(pollId) {
  if (!pollId) throw new Error('Missing poll id');
  const nextToken = typeof crypto !== 'undefined' && crypto?.randomUUID
    ? crypto.randomUUID()
    : UUID_FALLBACK();

  const { data, error } = await supabase
    .from('meal_polls')
    .update({
      share_token: nextToken,
      share_expires_at: null,
    })
    .eq('id', pollId)
    .select('id, share_token, share_expires_at')
    .single();

  if (error) throw error;

  return {
    shareToken: data.share_token,
    shareExpiresAt: data.share_expires_at || null,
    shareUrl: buildPollShareUrl(data.share_token),
  };
}

export async function fetchSharedPoll(shareToken) {
  if (!shareToken) throw new Error('Missing share token');
  const { data, error } = await supabase.rpc(
    'fetch_shared_poll',
    {
      p_share_token: shareToken,
    }
  );
  if (error) {
    const inactiveError = new Error(error?.message || 'Poll unavailable');
    inactiveError.code = error?.code ?? error?.hint ?? 'unknown';
    inactiveError.status = error?.status ?? error?.details ?? 400;
    inactiveError.details = error;
    throw inactiveError;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    id: row.poll_id,
    teamId: row.team_id,
    title: row.title,
    description: row.description || '',
    customMessage: row.custom_message || '',
    allowMultiple: Boolean(row.allow_multiple),
    allowSuggestions: Boolean(row.allow_suggestions),
    anonymous: Boolean(row.anonymous),
    expiresAt: row.expires_at || null,
    status: row.poll_status,
    targetRoles: Array.isArray(row.target_roles) ? row.target_roles : [],
    requiresTracking: Boolean(row.requires_tracking),
    creatorName: row.creator_name || '',
    options: Array.isArray(row.options)
      ? row.options.map((opt) => ({
          id: opt.id,
          label: opt.label,
          description: opt.description,
          cuisineType: opt.cuisine_type,
          voteCount: opt.vote_count || 0,
          voters: normalizeVoters(opt.voters || []),
        }))
      : [],
    totalVotes: Number(row.total_votes) || 0,
  };
}

// `shareToken` + `email` is the shared-poll page's path. poll_votes is only
// readable by a row's own user_id (which a share-link vote never sets) or a
// coach, so the direct select returned nothing and a returning voter looked
// like they had never voted. The RPC resolves the member from the email.
export async function fetchMemberVote(pollId, memberId, { shareToken = null, email = null } = {}) {
  if (shareToken && email) {
    const { data, error } = await supabase.rpc('fetch_shared_poll_member_vote', {
      p_share_token: shareToken,
      p_email: email,
    });
    if (error) throw error;
    return (Array.isArray(data) ? data : []).filter(Boolean);
  }
  if (!pollId || !memberId) return [];
  const { data, error } = await supabase
    .from('poll_votes')
    .select('option_id')
    .eq('poll_id', pollId)
    .eq('member_id', memberId);
  if (error) throw error;
  return (data || []).map((row) => row.option_id).filter(Boolean);
}

export async function joinPollWithEmail(pollId, email) {
  if (!pollId) throw new Error('Missing poll id');
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) throw new Error('Enter an email to continue');

  const { data, error } = await supabase.rpc('join_poll_with_email', {
    p_poll_id: pollId,
    p_email: normalizedEmail,
  });

  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('Unable to verify email for this poll');
  return {
    pollId: row.poll_id,
    teamId: row.team_id,
    memberId: row.member_id,
    fullName: row.full_name,
    email: row.email || normalizedEmail,
    phoneNumber: row.phone_number || null,
    // The RPC never returns the number itself (migration 065), only whether
    // one is on file (088). A database without 088 reads as no phone, so the
    // page offers no opt-in rather than one with nothing to text.
    hasPhone: row.has_phone === true,
    smsOptIn: typeof row.sms_opt_in === 'boolean' ? row.sms_opt_in : null,
    smsOptInRecordedAt: row.sms_opt_in_recorded_at || null,
  };
}

// `email` is the proof of identity: submit_shared_poll_vote (migration 075)
// refuses a member id the caller cannot back with that member's email or a
// matching signed-in account — before, any link holder could replace any
// teammate's vote by sending their id.
export async function submitSharedPollVote({ shareToken, memberId, optionIds, email = null }) {
  if (!shareToken) throw new Error('Missing share token');
  if (!memberId) throw new Error('Missing member id');
  const selections = Array.isArray(optionIds) ? optionIds.filter(Boolean) : [];
  if (!selections.length) {
    throw new Error('Select at least one option');
  }

  const { data, error } = await supabase.rpc('submit_shared_poll_vote', {
    p_share_token: shareToken,
    p_member_id: memberId,
    p_option_ids: selections,
    p_email: email || null,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    id: row.poll_id,
    teamId: row.team_id,
    title: row.title,
    description: row.description || '',
    customMessage: row.custom_message || '',
    allowMultiple: Boolean(row.allow_multiple),
    allowSuggestions: Boolean(row.allow_suggestions),
    anonymous: Boolean(row.anonymous),
    expiresAt: row.expires_at || null,
    status: row.poll_status,
    targetRoles: Array.isArray(row.target_roles) ? row.target_roles : [],
    requiresTracking: Boolean(row.requires_tracking),
    creatorName: row.creator_name || '',
    options: Array.isArray(row.options)
      ? row.options.map((opt) => ({
          id: opt.id,
          label: opt.label,
          description: opt.description,
          cuisineType: opt.cuisine_type,
          voteCount: opt.vote_count || 0,
          voters: normalizeVoters(opt.voters || []),
        }))
      : [],
    totalVotes: Number(row.total_votes) || 0,
    viewer: row.viewer
      ? {
          memberId: row.viewer.memberId || null,
          email: row.viewer.email || null,
          fullName: row.viewer.fullName || null,
          optionIds: Array.isArray(row.viewer.optionIds)
            ? row.viewer.optionIds.filter(Boolean)
            : [],
        }
      : null,
  };
}

// The vote named an option the coach has removed since the page loaded
// (migration 097). Nothing was recorded and the earlier vote still stands.
export const isRemovedPollOptionError = (error) => error?.hint === 'option_removed';

export const subscribeToPollVotes = (pollId, callback) => {
  if (!pollId || typeof callback !== 'function') return null;
  const channel = supabase
    .channel(`public:poll_votes:${pollId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'poll_votes',
        filter: `poll_id=eq.${pollId}`,
      },
      callback
    )
    .subscribe();
  return channel;
};

export const unsubscribeFromChannel = (channel) => {
  if (channel?.unsubscribe) {
    channel.unsubscribe();
  }
};

export default {
  fetchTeamPolls,
  fetchPollById,
  createPoll,
  updatePoll,
  updatePollStatus,
  deletePoll,
  regeneratePollShareToken,
  fetchSharedPoll,
  joinPollWithEmail,
  submitSharedPollVote,
  fetchMemberVote,
  subscribeToPollVotes,
  unsubscribeFromChannel,
  buildPollShareUrl,
};
